import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { authService } from '../services/authService';
import { MsalAuthService } from '../auth/msalAuthService';
import { useNavigate } from 'react-router-dom';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  loginWithSocial: (provider: string) => Promise<void>;
  createGuestUser: (displayName: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return { ...state, isLoading: false };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_USER':
      localStorage.setItem('user', JSON.stringify(action.payload));
      return { ...state, user: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing token on app start
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
      }
    }
    
    dispatch({ type: 'SET_LOADING', payload: false });
    
    // Add listener for auth state changes from MSAL authentication
    const handleAuthStateChanged = (event: CustomEvent) => {
      console.log('Auth state changed event received:', event.detail);
      if (event.detail && event.detail.user && event.detail.token) {
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { user: event.detail.user, token: event.detail.token } 
        });
      }
    };

    // Add listener for token refresh failures
    const handleTokenExpired = (event: Event) => {
      console.log('Token expired event received');
      // Logout and redirect to login page with expired message
      logout();
      navigate('/login?expired=true');
    };
    
    // Add event listeners
    window.addEventListener('authStateChanged', handleAuthStateChanged as EventListener);
    window.addEventListener('tokenExpired', handleTokenExpired);
    
    // Clean up
    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChanged as EventListener);
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, [navigate]);

  const login = async (email: string, password: string): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authService.login(email, password);
      if (response.success) {
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { user: response.data.user, token: response.data.token } 
        });
        return true;
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        return false;
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      return false;
    }
  };

  const loginWithSocial = async (provider: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      // Use MSAL for all social providers
      try {
        // Configure the login request with the appropriate provider
        // The actual provider selection happens in the MSAL popup
        const authResult = await MsalAuthService.loginWithPopup();
        if (authResult) {
          // After successful login, check if token and user were stored in localStorage
          const storedToken = localStorage.getItem('token');
          const storedUserStr = localStorage.getItem('user');
          
          if (storedToken && storedUserStr) {
            try {
              // Parse the stored user and update the state
              const storedUser = JSON.parse(storedUserStr);
              console.log('Found stored user data after social login:', storedUser);
              
              // Update the state with the stored token and user
              dispatch({ 
                type: 'LOGIN_SUCCESS', 
                payload: { user: storedUser, token: storedToken } 
              });
              return;
            } catch (parseError) {
              console.error('Error parsing stored user data:', parseError);
            }
          }
          
          // If we couldn't get the user from localStorage, try to refresh from API
          console.log('No stored user data found, trying to refresh from API');
          await refreshUser();
        } else {
          dispatch({ type: 'LOGIN_FAILURE' });
        }
      } catch (msalError) {
        console.error(`MSAL authentication error for ${provider}:`, msalError);
        dispatch({ type: 'LOGIN_FAILURE' });
        throw msalError;
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const createGuestUser = async (displayName: string): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authService.createGuestUser(displayName);
      if (response.success) {
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { user: response.data.user, token: response.data.token } 
        });
        return true;
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        return false;
      }
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      return false;
    }
  };

  const logout = async () => {
    try {
      // Call the API logout endpoint
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Always dispatch logout action to clear state
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshUser = async () => {
    if (state.token) {
      try {
        const response = await authService.getCurrentUser();
        if (response.success) {
          dispatch({ type: 'SET_USER', payload: response.data });
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithSocial,
    createGuestUser,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
